import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@radix-ui/react-popover';
import { NotificationListData } from '@shared/types';
import { Link } from '@tanstack/react-router';
import { useListNotificationsUnread } from '@web/hooks/notification';
import { BellRing, LayoutDashboard, Mail } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';

import { Route } from '../route';
import { unixTimeToAgo } from '@shared/utils';

const MAX_DESCRIPTION_LENGTH = 100;

export function DashboardHeader() {
  const { toggleNavbarOpen } = Route.useRouteContext();

  const [unreadNotifications, setUnreadNotifications] = useState<number>(0);
  const [notifications, setNotifications] = useState<NotificationListData[]>(
    [],
  );
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const {
    data: notificationsData,
    isLoading,
    error,
  } = useListNotificationsUnread();

  useEffect(() => {
    if (error) {
      toast.error('Error fetching notifications');
    }

    if (!isLoading && notificationsData) {
      const unreadCount = notificationsData.filter(
        (notification) => notification.status === 'new',
      ).length;

      setUnreadNotifications(unreadCount);
      setNotifications(
        notificationsData
          .map((n) => ({
            ...n,
            description: n.description ?? '',
            isRead: n.status !== 'new',
            createdAt: new Date(n.createdAt),
            parentRecordId: n.parentTableId,
          }))
          .slice(0, 5),
      );
    }
  }, [notificationsData, isLoading, error]);

  const handleViewAllNotifications = () => {
    setIsPopoverOpen(false);
  };

  const renderNotificationDescription = (
    notification: NotificationListData,
  ) => {
    if (!notification.description) {
      return 'No description';
    }

    if (notification.description.length <= MAX_DESCRIPTION_LENGTH) {
      return notification.description;
    }

    return (
      <span>
        {`${notification.description.slice(0, MAX_DESCRIPTION_LENGTH)}...`}
        <Link
          to="/dashboard/admin/notification"
          className="ml-2 hover:underline text-primary"
          onClick={() => {
            handleViewAllNotifications();
          }}
        >
          See more
        </Link>
      </span>
    );
  };

  return (
    <header className="h-mobile-header md:h-desktop-header flex items-center justify-between gap-8 border-b px-10 py-3">
      <div>
        <LayoutDashboard
          onClick={() => {
            toggleNavbarOpen();
          }}
        />
      </div>

      <div className="mx-4 flex items-center gap-8 justify-self-end">
        <Popover open={isPopoverOpen} onOpenChange={setIsPopoverOpen}>
          <PopoverTrigger>
            <motion.div
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              className="relative"
            >
              <BellRing className="ml-2 h-5 w-5" />
              <AnimatePresence>
                {unreadNotifications > 0 && (
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    exit={{ scale: 0 }}
                    className="absolute right-0 top-0 h-2 w-2 rounded-full bg-red-500"
                  />
                )}
              </AnimatePresence>
            </motion.div>
          </PopoverTrigger>

          <PopoverContent
            className="z-50 w-96 rounded-sm border bg-white shadow-floating"
            onInteractOutside={() => { setIsPopoverOpen(false); }}
            onEscapeKeyDown={() => { setIsPopoverOpen(false); }}
          >
            <AnimatePresence>
              <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 20 }}
                transition={{ duration: 0.3 }}
              >
                {notifications.length === 0 ? (
                  <p>There are no new notifications.</p>
                ) : (
                  notifications.map((notification) => (
                    <motion.div
                      key={notification.id}
                      className="shadow-floating rounded-sm p-2 hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex justify-between">
                        <p className="font-bold text-sm">{notification.title}</p>
                        <p className="text-xs text-gray-500 mt-1 text-right">
                          {unixTimeToAgo(notification.createdAt.getTime())}
                        </p>
                      </div>
                      <p className="text-sm">
                        {renderNotificationDescription(notification)}
                      </p>
                    </motion.div>
                  ))
                )}

                <motion.div
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Link
                    to="/dashboard/admin/notification"
                    className="mt-2 block text-center text-primary hover:underline"
                    onClick={handleViewAllNotifications}
                  >
                    View all notifications
                  </Link>
                </motion.div>
              </motion.div>
            </AnimatePresence>
          </PopoverContent>
        </Popover>

        <Mail className="size-5" />
      </div>
    </header>
  );
}
